import React, { createContext, useState, useEffect, useContext } from 'react';
import authService from '../services/authService';

const AuthContext = createContext();

// Helper function to check admin status
const checkAdminStatus = (userData) => {
  return userData?.is_staff || userData?.is_superuser || userData?.isAdmin || false;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      // Check if we have a JWT token (not cookies!)
      const token = localStorage.getItem('access_token');
      
      if (token) {
        // Verify token is still valid by getting user profile
        const userData = await authService.getProfile();
        
        // Add admin status to user object
        const userWithAdmin = {
          ...userData,
          isAdmin: checkAdminStatus(userData)
        };
        
        setUser(userWithAdmin);
        localStorage.setItem('user', JSON.stringify(userWithAdmin));
      } else {
        // No token, ensure user is null
        setUser(null);
      }
    } catch (error) {
      console.error('Failed to load user:', error);
      // If token is invalid, clear everything
      authService.logout();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      setError(null);
      setLoading(true);
      
      const data = await authService.login(username, password);
      
      // Get user profile to check admin status
      const userData = await authService.getProfile();
      
      // Add admin status to user object
      const userWithAdmin = {
        ...userData,
        isAdmin: checkAdminStatus(userData)
      };
      
      setUser(userWithAdmin);
      localStorage.setItem('user', JSON.stringify(userWithAdmin));
      
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.error || 
                          'Login failed. Please check your credentials.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      setLoading(true);
      
      const data = await authService.register(userData);
      
      // If registration includes auto-login, set user
      if (data.user || data.access) {
        const currentUser = authService.getCurrentUser();
        if (currentUser) {
          const userWithAdmin = {
            ...currentUser,
            isAdmin: checkAdminStatus(currentUser)
          };
          setUser(userWithAdmin);
          localStorage.setItem('user', JSON.stringify(userWithAdmin));
        }
      }
      
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.error || 
                          'Registration failed. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setError(null);
  };

  const updateProfile = async (profileData) => {
    try {
      setError(null);
      setLoading(true);
      
      const updatedUser = await authService.updateProfile(profileData);
      
      // Maintain admin status
      const userWithAdmin = {
        ...updatedUser,
        isAdmin: user?.isAdmin || checkAdminStatus(updatedUser)
      };
      
      setUser(userWithAdmin);
      localStorage.setItem('user', JSON.stringify(userWithAdmin));
      
      return userWithAdmin;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.error || 
                          'Failed to update profile.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const verifyEmail = async (token) => {
    try {
      setError(null);
      setLoading(true);
      
      const response = await authService.verifyEmail(token);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.error || 
                          'Email verification failed.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (passwordData) => {
    try {
      setError(null);
      setLoading(true);
      
      const response = await authService.changePassword(passwordData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.error || 
                          'Failed to change password.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const requestPasswordReset = async (email) => {
    try {
      setError(null);
      setLoading(true);
      
      const response = await authService.requestPasswordReset(email);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.error || 
                          'Failed to request password reset.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (token, password) => {
    try {
      setError(null);
      setLoading(true);
      
      const response = await authService.resetPassword(token, password);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.error || 
                          'Failed to reset password.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Check if user has admin privileges - ONLY for React app admin features
  const hasAdminAccess = () => {
    // This checks React app admin features, NOT Django admin access
    return user?.isAdmin || false;
  };

  // Check JWT token status (not cookies)
  const isAuthenticated = () => {
    return !!localStorage.getItem('access_token');
  };

  // Refresh user data
  const refreshUser = async () => {
    try {
      const userData = await authService.getProfile();
      const userWithAdmin = {
        ...userData,
        isAdmin: checkAdminStatus(userData)
      };
      setUser(userWithAdmin);
      localStorage.setItem('user', JSON.stringify(userWithAdmin));
      return userWithAdmin;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      register, 
      logout, 
      loading,
      error,
      loadUser,
      updateProfile,
      verifyEmail,
      changePassword,
      requestPasswordReset,
      resetPassword,
      hasAdminAccess,
      isAuthenticated,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};